import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/api/client';
import type { OrderListItem, Page } from '@/api/types';
import type { ProductListItem } from '@/features/products/types';
import type { Customer } from '@/features/partners/types';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import { useFormFactor } from '@/lib/formFactor';
import { orderStatus } from '@/lib/status';
import s from './GlobalSearch.module.css';

type Scope = 'all' | 'orders' | 'products' | 'customers';
type Results = { orders: Page<OrderListItem> | null; products: Page<ProductListItem> | null; customers: Page<Customer> | null };
type Entry = { group: Exclude<Scope, 'all'>; id: string; to: string; title: string; detail: string; status?: string; meta?: string; initial?: string; available?: number };
const EMPTY: Results = { orders: null, products: null, customers: null };
const SCOPES: { key: Scope; label: string }[] = [
  { key: 'all', label: '全部' }, { key: 'orders', label: '订单' }, { key: 'products', label: '产品' }, { key: 'customers', label: '客户' },
];
const recentKey = (userId: string) => `ims.v2.global-search.${userId}`;

export function openGlobalSearch() { window.dispatchEvent(new Event('ims:v2:open-search')); }

function Highlight({ text, query }: { text: string; query: string }) {
  const at = text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
  if (!query || at < 0) return <>{text}</>;
  return <>{text.slice(0, at)}<mark>{text.slice(at, at + query.length)}</mark>{text.slice(at + query.length)}</>;
}

function maskPhone(phone?: string | null) {
  if (!phone) return '';
  return phone.length >= 7 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : phone;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const ff = useFormFactor();
  const user = useSession((state) => state.user);
  const can = useSession((state) => state.can);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [recent, setRecent] = useState<string[]>([]);
  const [results, setResults] = useState<Results>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const permissions = {
    orders: can('orders.read.all', 'orders.read.own', 'orders.read.assigned'),
    products: can('products.read'),
    customers: can('customers.read', 'taobao.manage'),
  };

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.matches('input,textarea,select,[contenteditable="true"]');
      if ((event.key === '/' || (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey))) && !typing) {
        event.preventDefault(); setOpen(true);
      }
    };
    window.addEventListener('ims:v2:open-search', onOpen);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('ims:v2:open-search', onOpen); window.removeEventListener('keydown', onKey); };
  }, []);
  useEffect(() => {
    if (!open || !user?.id) return;
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(recentKey(user.id)) || '[]');
      setRecent(Array.isArray(saved) ? saved.filter((value): value is string => typeof value === 'string').slice(0, 10) : []);
    } catch { setRecent([]); }
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, user?.id]);
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); setQuery(''); setScope('all'); }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || !q) { setResults(EMPTY); setLoading(false); setError(''); return; }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true); setError('');
      const requested = [permissions.orders, permissions.products, permissions.customers];
      const fetched = await Promise.allSettled([
        requested[0] ? api.get<Page<OrderListItem>>('/api/orders', { keyword: q, pageSize: 3 }, controller.signal) : Promise.resolve(null),
        requested[1] ? api.get<Page<ProductListItem>>('/api/products', { keyword: q, pageSize: 3 }, controller.signal) : Promise.resolve(null),
        requested[2] ? api.get<Page<Customer>>('/api/customers', { keyword: q, pageSize: 3 }, controller.signal) : Promise.resolve(null),
      ]);
      if (controller.signal.aborted) return;
      const next: Results = {
        orders: fetched[0].status === 'fulfilled' ? fetched[0].value : null,
        products: fetched[1].status === 'fulfilled' ? fetched[1].value : null,
        customers: fetched[2].status === 'fulfilled' ? fetched[2].value : null,
      };
      setResults(next); setSelected(0); setLoading(false);
      if (requested.some(Boolean) && requested.every((active, index) => !active || fetched[index].status === 'rejected')) setError('搜索失败，请稍后重试。');
    }, 260);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [open, query, permissions.orders, permissions.products, permissions.customers]);

  if (!open) return null;
  const q = query.trim();
  const saveRecent = (value: string) => {
    if (!user?.id || !value) return;
    const next = [value, ...recent.filter((item) => item !== value)].slice(0, 10);
    setRecent(next);
    try { localStorage.setItem(recentKey(user.id), JSON.stringify(next)); } catch { /* storage unavailable */ }
  };
  const close = () => { setOpen(false); setQuery(''); setScope('all'); setResults(EMPTY); };
  const entries: Entry[] = [];
  if (scope === 'all' || scope === 'orders') for (const order of results.orders?.items || []) {
    const platformHit = order.platformOrderNo?.toLocaleLowerCase().includes(q.toLocaleLowerCase());
    entries.push({ group: 'orders', id: order.id, to: `/orders/${order.id}`, title: order.orderNo, detail: `${platformHit ? `1688 单号 ${order.platformOrderNo} · ` : ''}${order.customer.name} · ${order.lines[0]?.product.name || '订单'} · 交货 ${order.deliveryDate?.slice(5, 10) || '—'}`, status: orderStatus[order.status]?.label });
  }
  if (scope === 'all' || scope === 'customers') for (const customer of results.customers?.items || []) entries.push({ group: 'customers', id: customer.id, to: `/customers/${customer.id}`, title: customer.name, detail: [customer.contact, maskPhone(customer.phone), customer.ownerUser?.name ? `业务员 ${customer.ownerUser.name}` : ''].filter(Boolean).join(' · '), initial: customer.name.trim().slice(0, 1) });
  if (scope === 'all' || scope === 'products') for (const product of results.products?.items || []) {
    const quote = product.packagingOptions.flatMap((option) => option.supplierQuotes || []).find((item) => item.supplier?.name.toLocaleLowerCase().includes(q.toLocaleLowerCase()));
    entries.push({ group: 'products', id: product.id, to: `/products/${product.id}`, title: product.name, meta: product.code, detail: quote?.supplier ? `${quote.isDefault ? '默认供应商' : '供应商'} ${quote.supplier.name}` : '', available: product.inventorySummary.availableQuantity });
  }
  const total = (results.orders?.pagination.total || 0) + (results.products?.pagination.total || 0) + (results.customers?.pagination.total || 0);
  const counts: Record<Scope, number> = { all: total, orders: results.orders?.pagination.total || 0, products: results.products?.pagination.total || 0, customers: results.customers?.pagination.total || 0 };
  const choose = (entry: Entry) => { saveRecent(q); close(); navigate(entry.to); };
  const scopes = SCOPES.filter(({ key }) => key === 'all' || permissions[key]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    else if (event.key === 'ArrowDown') { event.preventDefault(); setSelected((value) => Math.min(entries.length - 1, value + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setSelected((value) => Math.max(0, value - 1)); }
    else if (event.key === 'Enter' && entries[selected]) { event.preventDefault(); choose(entries[selected]); }
    else if (event.key === 'Tab' && ff !== 'phone') {
      event.preventDefault();
      const at = scopes.findIndex((item) => item.key === scope);
      setScope(scopes[(at + (event.shiftKey ? scopes.length - 1 : 1)) % scopes.length].key); setSelected(0);
    }
  };

  return <div className={s.overlay} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section className={`${s.panel} ${ff === 'phone' ? s.phone : ff === 'tablet' ? s.tablet : ''}`} role="dialog" aria-modal="true" aria-label="全局搜索">
      <header className={s.header}><Icon name="srch" size={20} /><input ref={input} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onKeyDown} placeholder="订单号、产品、客户" aria-label="搜索订单、产品、客户" />{q && <button type="button" className={s.clearQuery} aria-label="清除搜索" onClick={() => { setQuery(''); setScope('all'); input.current?.focus(); }}><Icon name="x" size={14} /></button>}<button type="button" onClick={close}>取消</button></header>
      <nav className={s.scopes} aria-label="搜索范围">{scopes.map(({ key, label }) => <button type="button" key={key} className={scope === key ? s.active : ''} onClick={() => { setScope(key); setSelected(0); }}>{label}{q && !loading ? ` ${counts[key]}` : ''}</button>)}</nav>
      <div className={s.body}>
        {!q ? <>
          <div className={s.sectionTitle}>最近搜索 <button type="button" onClick={() => { setRecent([]); if (user?.id) try { localStorage.removeItem(recentKey(user.id)); } catch { /* storage unavailable */ } }}>清除</button></div>
          <div className={s.recents}>{recent.length ? recent.map((item) => <button type="button" key={item} onClick={() => setQuery(item)}>{item}</button>) : <p>暂无最近搜索</p>}</div>
          <div className={s.sectionTitle}>可以这样搜</div>
          <div className={s.tips}><p>订单：XS 单号、1688 单号、客户名</p><p>产品：名称、编号、供应商</p><p>客户：名称、联系人、电话后四位</p></div>
        </> : <>
          {loading && <p className={s.state}>正在搜索…</p>}
          {error && <p className={s.state} role="alert">{error}</p>}
          {!loading && !error && !entries.length && <p className={s.state}>没有找到匹配结果</p>}
          {(['orders', 'customers', 'products'] as const).map((group) => {
            const rows = entries.filter((entry) => entry.group === group);
            if (!rows.length) return null;
            const label = group === 'orders' ? '订单' : group === 'customers' ? '客户' : '产品';
            return <div className={s.group} key={group}><div className={s.sectionTitle}>{label} · {counts[group]} <button type="button" onClick={() => { saveRecent(q); close(); navigate(`/${group}?q=${encodeURIComponent(q)}`); }}>在{label}里看全部 ›</button></div>
              {rows.map((entry) => <button type="button" key={entry.id} className={`${s.result} ${entries.indexOf(entry) === selected ? s.selected : ''}`} onMouseEnter={() => setSelected(entries.indexOf(entry))} onClick={() => choose(entry)}>
                {entry.group !== 'orders' ? <span className={s.entityBadge}>{entry.initial || <Icon name="box" size={16} />}</span> : ff === 'tablet' ? <span className={`${s.entityBadge} ${s.orderBadge}`}><Icon name="cart" size={16} /></span> : null}<span className={s.resultText}><span className={s.resultMain}><b><Highlight text={entry.title} query={q} /></b>{entry.meta && <small className={s.resultMeta}>{entry.meta}</small>}{entry.status && <em>{entry.status}</em>}</span>
                <span className={s.resultDetails}>{entry.detail && <small><Highlight text={entry.detail} query={q} /></small>}{entry.available !== undefined && <span className={s.resultAvailability}><b>{entry.available < 0 ? `−${Math.abs(entry.available)}` : entry.available}</b><small>可用</small></span>}</span></span>{ff === 'tablet' ? entries.indexOf(entry) === selected && <span className={s.enterKey}>↵</span> : <Icon name="chev" size={14} className={s.resultArrow} />}
              </button>)}
            </div>;
          })}
          <p className={s.note}>采购单、供应商、对账单在各自模块里搜。结果按你的数据范围过滤。</p>
        </>}
      </div>
      {ff === 'tablet' ? <footer className={`${s.footer} ${s.tabletFooter}`}><span><kbd>↑</kbd> <kbd>↓</kbd> 选择</span><span><kbd>↵</kbd> 打开</span><span><kbd>tab</kbd> 切换分组</span><span className={s.closeHint}><kbd>esc</kbd> 关闭</span></footer> : ff !== 'phone' && <footer className={s.footer}>↑ ↓ 选择　↵ 打开　Tab 切换分组　Esc 关闭</footer>}
    </section>
  </div>;
}
